import type { BandColumn, FormatTemplateInput } from './types';
import { type Banda, altoImagenMm, columnasDe, htmlDeBloque } from './bandas';
import { hojaMm } from './regla';
import { purificarAFragmento, sanitizeBandaRicaSalida } from '@/lib/sanitize-html';
import { FUENTES_LIBRES, nombreCss } from '@/lib/orders/fuentes-editor';

/*
 * ENCABEZADO Y PIE EN UN SOLO CAMPO («banda rica», DISENO.md de banda-unica).
 * La banda se guarda como UNA columna `{ type:'text', align, text, richHtml }`
 * dentro de headerColumns/footerColumns: un processing-ms viejo la sigue
 * entendiendo como una columna de texto y pinta su `text`.
 *
 * `richHtml` es HTML CANÓNICO de lista blanca (§2): párrafos alineados, como
 * mucho una tabla de 2 a 4 celdas, negrita/cursiva/subrayado, imágenes y el
 * token del número. El back lo vuelve a sanear con la misma regla
 * (htmlparser2): los vectores compartidos (`banda-rica.vectores.json`)
 * fijan que los dos lados den la misma cadena. Cualquier cambio de la regla
 * se hace en los dos lados a la vez.
 */

export type Alineacion = 'left' | 'center' | 'right';
export type MarcaPerfil = 'logo' | 'signature' | 'seal';

/** Topes de la banda (§1.3): los mismos en el front y en el back. */
export const TOPES = {
  renglones: 8,
  celdasMin: 2,
  celdasMax: 4,
  imagenes: 4,
  altoApiladoMm: 90,
  texto: 1000,
  html: 8000,
  entrada: 50000,
  profundidad: 32,
  altoImgMin: 5,
  altoImgMax: 60,
  altoImgDefecto: 15,
  respaldoRenglones: 4,
  respaldoTexto: 300,
} as const;

/** Bloques de la regla 2: cada uno abre párrafo. */
const BLOQUES = ['p', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'li', 'blockquote', 'pre', 'address', 'section', 'article', 'header', 'footer', 'figure', 'figcaption', 'dt', 'dd'];
const CONTENEDORES = ['ul', 'ol', 'dl', 'table', 'thead', 'tbody', 'tfoot', 'tr', 'td', 'th', 'br', 'img'];
const EN_LINEA = ['strong', 'b', 'em', 'i', 'u', 'ins', 'span', 'font', 'a', 'sub', 'sup', 'small', 'code', 'label', 'mark', 's', 'strike', 'del', 'big', 'abbr', 'cite', 'q', 'time', 'var', 'kbd', 'samp'];

/** Lo que se reconoce; lo demás se desenvuelve y su `style` no se lee. Idéntica en el back. */
export const ETIQUETAS_CONOCIDAS: readonly string[] = [...BLOQUES, ...CONTENEDORES, ...EN_LINEA];
/** Regla 1: se borran con todo su contenido. Sustituye a la lista de serie de DOMPurify (que incluye `thead`). */
export const BORRAR_CON_CONTENIDO: readonly string[] = [
  'script', 'style', 'template', 'iframe', 'object', 'embed', 'noscript', 'svg', 'math', 'textarea', 'select', 'button', 'title', 'head',
  'xmp', 'noembed', 'noframes', 'plaintext',
];
/** Configuración EXACTA de DOMPurify (§2.2): con otra, el árbol del front no sería el que recorre el back. */
export const CONFIG_PURIFICADOR = {
  ALLOWED_TAGS: [...ETIQUETAS_CONOCIDAS],
  ALLOWED_ATTR: ['style', 'src', 'height', 'data-ez-img', 'data-ez-campo'],
  ALLOW_DATA_ATTR: false,
  KEEP_CONTENT: true,
  FORBID_CONTENTS: [...BORRAR_CON_CONTENIDO],
  // Sin esto, lo que va al principio se parsea en el <head>: un <noscript>
  // inicial suelta su contenido en el body en vez de borrarse (el back,
  // htmlparser2, no tiene <head> y lo borra).
  FORCE_BODY: true,
  // El fragmento solo se LEE (la salida se reconstruye desde el modelo y nunca
  // se inserta), así que las defensas anti-mXSS de SAFE_FOR_XML no aportan y
  // cambian el árbol: borraban entero un <p> con «<3» junto a un comentario
  // (pegado de Word) y el `style` que contiene «-->». El back no lo hace.
  SAFE_FOR_XML: false,
};

const SET_BLOQUES = new Set(BLOQUES);
const SET_CONOCIDAS = new Set(ETIQUETAS_CONOCIDAS);
const SET_BORRAR = new Set(BORRAR_CON_CONTENIDO);
/** Contenedores de la regla 2 (y las piezas de una tabla anidada): sin párrafo propio, pero cortan como un bloque. */
const SET_CONTENEDORES = new Set(['ul', 'ol', 'dl', 'thead', 'tbody', 'tfoot', 'tr']);
const ESTRUCTURA_TABLA = new Set(['table', 'thead', 'tbody', 'tfoot', 'tr']);
/** Regla 9: el `style` de la tabla, sus filas y sus celdas solo aporta la alineación, nunca marcas (como el back). */
const FAMILIA_TABLA = new Set([...ESTRUCTURA_TABLA, 'td', 'th']);

export const TOKEN_NUMERO = '<span data-ez-campo="numero">#</span>';

// ---------------------------------------------------------------------------
// Orígenes de imagen (§2.3)
// ---------------------------------------------------------------------------

const UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
const RUTA_IMAGEN = `/format-templates/images/${UUID}/${UUID}\\.(?:png|jpe?g|webp)`;
const RE_PROPIA = new RegExp(`^https://(?:ezdocu-backend-production|ezdocu-backend-staging|ezdocu-backend-qa)\\.up\\.railway\\.app${RUTA_IMAGEN}$`, 'i');
const escaparRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * El origen del gateway con el que este front sube las imágenes
 * (`subirImagenBanda`): el de NEXT_PUBLIC_API_URL o, sin ella, el de su
 * valor por defecto. Se lee en cada llamada: las pruebas lo vacían.
 */
function origenExtra(): string | null {
  try {
    return new URL(process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3001').origin;
  } catch {
    return null;
  }
}

/** ¿Es una imagen subida por el editor? Se mira el valor CRUDO del atributo, nunca `img.src`. */
export function esImagenPropia(url: unknown): boolean {
  if (typeof url !== 'string') return false;
  if (RE_PROPIA.test(url)) return true;
  const extra = origenExtra();
  return !!extra && new RegExp(`^${escaparRegex(extra)}${RUTA_IMAGEN}$`, 'i').test(url);
}

/** La misma lista como `ALLOWED_URI_REGEXP` de DOMPurify. */
export function reUriPropia(): RegExp {
  const extra = origenExtra();
  const fijos = '(?:https://(?:ezdocu-backend-production|ezdocu-backend-staging|ezdocu-backend-qa)\\.up\\.railway\\.app';
  return new RegExp(`^${fijos}${extra ? `|${escaparRegex(extra)}` : ''})${RUTA_IMAGEN}$`, 'i');
}

// ---------------------------------------------------------------------------
// Modelo
// ---------------------------------------------------------------------------

/** Un átomo de un renglón: un carácter (unidad UTF-16), una imagen o el número. `m` = negrita 1 | cursiva 2 | subrayado 4. */
export type Atomo =
  | { k: 'txt'; c: string; m: number }
  | { k: 'img'; marca: 'custom' | MarcaPerfil; src: string | null; mm: number; m: number }
  | { k: 'num' };
export type Renglon = Atomo[];
export interface Parrafo { tipo: 'p'; align: Alineacion; linea: Renglon }
export interface Celda { align: Alineacion; lineas: Renglon[] }
export interface Tabla { tipo: 'tabla'; celdas: Celda[] }
export type BloqueRico = Parrafo | Tabla;

export interface BandaRica {
  /** Canónico: lo que se guarda en `richHtml` ('' si la banda está vacía). */
  html: string;
  /** Respaldo plano (§2.6): el `text` de la columna. */
  texto: string;
  alineacion: Alineacion;
  tieneNumero: boolean;
  imagenes: number;
  vacia: boolean;
  /** Toda transformación que cambia lo que se ve (§2.2): el editor recarga si no está vacío. */
  descartes: string[];
  bloques: BloqueRico[];
}

const BIT_ETIQUETA: Record<string, number> = { strong: 1, b: 1, em: 2, i: 2, u: 4, ins: 4 };
const MARCAS: readonly ['strong', 'em', 'u'] = ['strong', 'em', 'u'];
const BIT_MARCA = { strong: 1, em: 2, u: 4 } as const;

/** Valor de una propiedad en un `style` ya en minúsculas: la primera, como `marcasDe` (bandas.ts). */
function regla(css: string, prop: string): string | undefined {
  return new RegExp(`(?:^|;)\\s*${prop}\\s*:\\s*([^;]*)`).exec(css)?.[1]?.trim();
}

/** Regla 5: la etiqueta y, por encima, su estilo. */
function marcasDe(tag: string, css: string, m: number): number {
  let pone = BIT_ETIQUETA[tag] ?? 0;
  let quita = 0;
  const peso = regla(css, 'font-weight');
  if (peso) { if (/^(?:bold|bolder|[6-9]00)$/.test(peso)) pone |= 1; else quita |= 1; }
  const estilo = regla(css, 'font-style');
  if (estilo) { if (/^(?:italic|oblique)/.test(estilo)) pone |= 2; else quita |= 2; }
  const deco = regla(css, 'text-decoration-line') ?? regla(css, 'text-decoration');
  if (deco) { if (/underline/.test(deco)) pone |= 4; else if (/^none/.test(deco)) quita |= 4; }
  return (m | (pone & ~quita)) & ~quita;
}

interface Estado { descartes: Set<string>; tablaVista: boolean; salida: Item[] }

/** Regla 4. */
function alineacionDe(css: string, est: Estado): Alineacion | null {
  const a = regla(css, 'text-align');
  if (!a) return null;
  if (a === 'left' || a === 'start') return 'left';
  if (a === 'center') return 'center';
  if (a === 'right' || a === 'end') return 'right';
  if (a === 'justify') { est.descartes.add('justify'); return 'left'; }
  return null;
}

const FACTOR_MM: Record<string, number> = { mm: 1, px: 25.4 / 96, pt: 25.4 / 72, cm: 10 };

/** Regla 10: alto en mm, entero de 5 a 60. */
function altoDeImagen(el: Element, est: Estado): number {
  let mm: number | null = null;
  const h = regla((el.getAttribute('style') ?? '').toLowerCase(), 'height');
  const x = h ? /^(\d+(?:\.\d+)?|\.\d+)(mm|px|pt|cm)$/.exec(h) : null;
  if (x) mm = Number(x[1]) * FACTOR_MM[x[2]];
  if (mm == null) {
    const attr = (el.getAttribute('height') ?? '').trim();
    if (/^(?:\d+(?:\.\d+)?|\.\d+)$/.test(attr)) mm = Number(attr) * FACTOR_MM.px;
  }
  const r = Math.round(mm != null && Number.isFinite(mm) ? mm : TOPES.altoImgDefecto);
  if (r < TOPES.altoImgMin || r > TOPES.altoImgMax) est.descartes.add('alto-imagen');
  return Math.min(TOPES.altoImgMax, Math.max(TOPES.altoImgMin, r));
}

// ---------------------------------------------------------------------------
// Recorrido del árbol (§2.2, reglas 1-12)
// ---------------------------------------------------------------------------

type Pieza = Atomo | { k: 'br' };
interface CeldaCruda { align: Alineacion; lineas: Renglon[] }
type Item = { tipo: 'p'; align: Alineacion; renglones: Renglon[] } | { tipo: 'tabla'; filas: CeldaCruda[][] };
interface Marco { align: Alineacion | null; hijos: number; raiz: boolean; tramo: Pieza[] }
interface Ctx { m: number; prof: number; enCelda: boolean }

const esBlancoAscii = (p: Pieza) => p.k === 'txt' && p.c === ' ';

/** Un tramo forma párrafo suelto si tiene algo visible o algún <br> (regla 2). */
const tramoVisible = (t: Pieza[]) => t.some((p) => !esBlancoAscii(p));

/**
 * Renglones de un tramo. Un <br> abre renglón salvo que sea lo último del
 * tramo: detrás no le sigue texto que no sea blanco ASCII, ni imagen, ni
 * número, ni otro <br> (regla 3, como lo pinta el navegador).
 */
function partir(t: Pieza[]): Renglon[] {
  let ultimo = -1;
  for (let i = t.length - 1; i >= 0; i--) if (!esBlancoAscii(t[i])) { ultimo = i; break; }
  const out: Renglon[] = [[]];
  t.forEach((p, i) => {
    if (p.k === 'br') { if (i < ultimo) out.push([]); return; }
    out[out.length - 1].push(p);
  });
  return out;
}

class Recolector {
  pila: Marco[];
  /** En una celda: la alineación del primer párrafo interior que la tenga. */
  primeraAlineacion: Alineacion | null = null;
  constructor(public salida: Item[], celda: boolean) {
    this.pila = [{ align: null, hijos: 0, raiz: !celda, tramo: [] }];
  }
  get actual(): Marco { return this.pila[this.pila.length - 1]; }
  pieza(p: Pieza) { this.actual.tramo.push(p); }
  /** Un bloque o una tabla: lo suelto de antes forma párrafo solo si se ve. */
  limite() {
    const m = this.actual;
    m.hijos++;
    this.volcar(m, false);
  }
  abrir(propia: Alineacion | null) {
    if (propia && !this.primeraAlineacion) this.primeraAlineacion = propia;
    const padre = this.actual;
    this.limite();
    this.pila.push({ align: propia ?? padre.align, hijos: 0, raiz: false, tramo: [] });
  }
  cerrar() {
    const m = this.pila.pop()!;
    // Un bloque sin bloques dentro es un párrafo aunque esté vacío.
    this.volcar(m, !m.raiz && m.hijos === 0);
  }
  volcar(m: Marco, siempre: boolean) {
    const t = m.tramo;
    m.tramo = [];
    if (!siempre && !tramoVisible(t)) return;
    this.salida.push({ tipo: 'p', align: m.align ?? 'left', renglones: partir(t) });
  }
  fin() {
    while (this.pila.length > 1) this.cerrar();
    const raiz = this.pila[0];
    this.volcar(raiz, !raiz.raiz && raiz.hijos === 0);
  }
}

/** Regla 7: fuera los controles C0 (salvo \t\n\r), DEL, U+200B y U+FEFF; cada blanco ASCII pasa a espacio. */
function texto(s: string | null, m: number, rec: Recolector) {
  const limpio = String(s ?? '')
    .replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f\u200b\ufeff]/g, '')
    .replace(/[ \t\n\r]/g, ' ');
  // Por unidades UTF-16: los topes cuentan así y el corte evita partir un par sustituto.
  for (let i = 0; i < limpio.length; i++) rec.pieza({ k: 'txt', c: limpio[i], m });
}

/** Lo que está más hondo que el tope de anidado queda solo como texto (sin recursión). */
function soloTexto(raiz: Element, m: number, rec: Recolector) {
  const pila: Node[] = [raiz];
  const orden: string[] = [];
  while (pila.length) {
    const n = pila.pop()!;
    if (n.nodeType === 3) { orden.push(n.nodeValue ?? ''); continue; }
    if (n.nodeType !== 1 || SET_BORRAR.has((n as Element).localName.toLowerCase())) continue;
    const hijos = n.childNodes;
    for (let i = hijos.length - 1; i >= 0; i--) pila.push(hijos[i]);
  }
  texto(orden.join(''), m, rec);
}

function imagen(el: Element, m: number, rec: Recolector, est: Estado) {
  const marca = el.getAttribute('data-ez-img');
  const mm = altoDeImagen(el, est);
  if (marca === 'logo' || marca === 'signature' || marca === 'seal') {
    rec.pieza({ k: 'img', marca, src: null, mm, m });
    return;
  }
  const src = el.getAttribute('src');
  if (src != null && esImagenPropia(src)) rec.pieza({ k: 'img', marca: 'custom', src, mm, m });
  else est.descartes.add('imagen-origen');
}

function recorrer(nodo: Node, ctx: Ctx, rec: Recolector, est: Estado) {
  const padre = nodo.nodeType === 1 ? (nodo as Element).localName.toLowerCase() : '';
  const enEstructura = ESTRUCTURA_TABLA.has(padre);
  for (const hijo of Array.from(nodo.childNodes)) {
    if (hijo.nodeType === 3) {
      // Texto suelto dentro de <table> o <tr>: HTML mal formado, no es de ninguna celda.
      if (!enEstructura) texto(hijo.nodeValue, ctx.m, rec);
      continue;
    }
    if (hijo.nodeType !== 1) continue;
    const el = hijo as Element;
    const tag = el.localName.toLowerCase();
    if (SET_BORRAR.has(tag)) continue;
    if (ctx.prof >= TOPES.profundidad) { soloTexto(el, ctx.m, rec); continue; }
    const conocida = SET_CONOCIDAS.has(tag);
    const css = conocida ? (el.getAttribute('style') ?? '').toLowerCase() : '';
    const sub: Ctx = { ...ctx, m: conocida && !FAMILIA_TABLA.has(tag) ? marcasDe(tag, css, ctx.m) : ctx.m, prof: ctx.prof + 1 };
    if (tag === 'br') { rec.pieza({ k: 'br' }); continue; }
    if (tag === 'img') { imagen(el, ctx.m, rec, est); continue; }
    if (tag === 'span' && el.getAttribute('data-ez-campo') === 'numero') {
      if (ctx.m) est.descartes.add('numero-marcas');
      rec.pieza({ k: 'num' });
      continue;
    }
    if (tag === 'table') {
      if (ctx.enCelda) {
        // Tabla dentro de una celda: sus celdas pasan a ser renglones de la de
        // fuera. Es un bloque: corta lo de antes y lo de después (como el back).
        est.descartes.add('tabla-anidada');
        rec.limite();
        recorrer(el, sub, rec, est);
        rec.limite();
      } else {
        rec.limite();
        tablaDePrimerNivel(el, sub, est);
      }
      continue;
    }
    if (SET_BLOQUES.has(tag) || tag === 'td' || tag === 'th') {
      if (tag === 'th') est.descartes.add('th');
      rec.abrir(alineacionDe(css, est));
      recorrer(el, sub, rec, est);
      rec.cerrar();
      continue;
    }
    if (SET_CONTENEDORES.has(tag)) {
      // Contenedor sin párrafo propio (regla 2), pero de bloque: `A<ul></ul>B`
      // son dos renglones en el navegador y en el back, y las filas de una
      // tabla anidada no se juntan aunque sus celdas pasen del tope de anidado.
      rec.limite();
      recorrer(el, sub, rec, est);
      rec.limite();
      continue;
    }
    recorrer(el, sub, rec, est);
  }
}

/** Filas de una tabla en orden de documento (de thead, tbody o tfoot), sin las de tablas anidadas. */
function filasDe(tabla: Element): Element[] {
  const out: Element[] = [];
  const visitar = (el: Element, prof: number) => {
    if (prof > TOPES.profundidad) return;
    for (const h of Array.from(el.children)) {
      const tag = h.localName.toLowerCase();
      if (tag === 'tr') out.push(h);
      else if (tag !== 'table' && tag !== 'td' && tag !== 'th' && !SET_BORRAR.has(tag)) visitar(h, prof + 1);
    }
  };
  visitar(tabla, 0);
  return out;
}

function celdasDe(tr: Element): Element[] {
  return Array.from(tr.children).filter((c) => {
    const tag = c.localName.toLowerCase();
    return tag === 'td' || tag === 'th';
  });
}

function celda(td: Element, ctx: Ctx, est: Estado): CeldaCruda {
  const tag = td.localName.toLowerCase();
  if (tag === 'th') est.descartes.add('th');
  const css = (td.getAttribute('style') ?? '').toLowerCase();
  const propia = alineacionDe(css, est);
  const salida: Item[] = [];
  const rec = new Recolector(salida, true);
  recorrer(td, { m: ctx.m, prof: ctx.prof + 1, enCelda: true }, rec, est);
  rec.fin();
  // Los renglones vacíos del final se quitan en el paso 2 (§1.3), DESPUÉS de
  // aplanar: una celda que pasa a párrafos los conserva, como en el back.
  const lineas = salida.flatMap((it) => (it.tipo === 'p' ? it.renglones : [])).map(limpiar);
  return { align: propia ?? rec.primeraAlineacion ?? 'left', lineas: lineas.length ? lineas : [[]] };
}

function tablaDePrimerNivel(tabla: Element, ctx: Ctx, est: Estado) {
  const filas = filasDe(tabla).map((tr) => celdasDe(tr).map((td) => celda(td, ctx, est)));
  if (!est.tablaVista) {
    est.tablaVista = true;
    est.salida.push({ tipo: 'tabla', filas });
    return;
  }
  // Una 2.ª tabla de primer nivel: párrafos con la alineación de cada celda.
  est.descartes.add('tabla-segunda');
  for (const fila of filas) for (const c of fila) est.salida.push({ tipo: 'p', align: c.align, renglones: c.lineas });
}

// ---------------------------------------------------------------------------
// Renglones: blancos, vacíos y variables
// ---------------------------------------------------------------------------

const VARIABLE = /\{[^{}\s<>]{1,40}\}/g;

/**
 * Regla 7 y 6 sobre un renglón: los blancos seguidos se quedan en uno (el
 * primero, como el navegador), fuera los de los bordes, un renglón de solo
 * espacios o NBSP queda vacío, y cada {variable} toma la máscara mayoritaria
 * (en empate, la primera que aparece) para que no se parta.
 */
function limpiar(r: Renglon): Renglon {
  const out: Renglon = [];
  for (const a of r) {
    if (a.k === 'txt' && a.c === ' ') {
      const prev = out[out.length - 1];
      if (!prev || (prev.k === 'txt' && prev.c === ' ')) continue;
    }
    out.push({ ...a });
  }
  while (out.length && out[out.length - 1].k === 'txt' && (out[out.length - 1] as { c: string }).c === ' ') out.pop();
  if (out.every((a) => a.k === 'txt' && (a.c === ' ' || a.c === '\u00a0'))) return [];
  // Variables, por tramos de texto (una imagen o el número los cortan).
  let i = 0;
  while (i < out.length) {
    if (out[i].k !== 'txt') { i++; continue; }
    let j = i;
    while (j < out.length && out[j].k === 'txt') j++;
    const tramo = out.slice(i, j) as { k: 'txt'; c: string; m: number }[];
    const s = tramo.map((a) => a.c).join('');
    for (const v of s.matchAll(VARIABLE)) {
      const desde = v.index ?? 0;
      const veces = new Map<number, number>();
      for (let k = desde; k < desde + v[0].length; k++) veces.set(tramo[k].m, (veces.get(tramo[k].m) ?? 0) + 1);
      const mayoria = [...veces.entries()].reduce((a, b) => (b[1] > a[1] ? b : a))[0];
      for (let k = desde; k < desde + v[0].length; k++) tramo[k].m = mayoria;
    }
    i = j;
  }
  return out;
}

/** En cada celda, fuera los renglones vacíos del final, dejando al menos uno. */
function recortarCelda(lineas: Renglon[]): Renglon[] {
  const out = lineas.slice();
  while (out.length > 1 && !out[out.length - 1].length) out.pop();
  return out.length ? out : [[]];
}

const parrafoVacio = (b: BloqueRico) => b.tipo === 'p' && !b.linea.length;

/** Regla 8: fuera los párrafos vacíos de los bordes de la banda y los renglones vacíos del final de cada celda. */
function bordes(bloques: BloqueRico[]): BloqueRico[] {
  let a = 0;
  let z = bloques.length;
  while (a < z && parrafoVacio(bloques[a])) a++;
  while (z > a && parrafoVacio(bloques[z - 1])) z--;
  return bloques.slice(a, z).map((b) => (b.tipo === 'tabla' ? { ...b, celdas: b.celdas.map((c) => ({ ...c, lineas: recortarCelda(c.lineas) })) } : b));
}

/** Los renglones en orden de documento (en la tabla, celda a celda y renglón a renglón). */
function lineasEnOrden(bloques: BloqueRico[]): Renglon[] {
  const out: Renglon[] = [];
  for (const b of bloques) {
    if (b.tipo === 'p') out.push(b.linea);
    else for (const c of b.celdas) out.push(...c.lineas);
  }
  return out;
}

function relimpiar(bloques: BloqueRico[]): BloqueRico[] {
  return bloques.map((b) => (b.tipo === 'p'
    ? { ...b, linea: limpiar(b.linea) }
    : { ...b, celdas: b.celdas.map((c) => ({ ...c, lineas: c.lineas.map(limpiar) })) }));
}

const maxImagen = (r: Renglon) => r.reduce((m, a) => (a.k === 'img' ? Math.max(m, a.mm) : m), 0);

/** Alto apilado de las imágenes (§1.3): renglón a renglón, la más alta; en la tabla el renglón i de todas las celdas cuenta una vez. */
export function altoApiladoMm(b: BandaRica | BloqueRico[]): number {
  const bloques = Array.isArray(b) ? b : b.bloques;
  let total = 0;
  for (const x of bloques) {
    if (x.tipo === 'p') { total += maxImagen(x.linea); continue; }
    const n = Math.max(0, ...x.celdas.map((c) => c.lineas.length));
    for (let i = 0; i < n; i++) total += Math.max(0, ...x.celdas.map((c) => maxImagen(c.lineas[i] ?? [])));
  }
  return total;
}

/** Renglones visibles: párrafos + renglones de la celda más larga. */
export function renglonesVisibles(b: BandaRica | BloqueRico[]): number {
  const bloques = Array.isArray(b) ? b : b.bloques;
  return bloques.reduce((n, x) => n + (x.tipo === 'p' ? 1 : Math.max(1, ...x.celdas.map((c) => c.lineas.length))), 0);
}

/** Quita átomos que cumplen `quitar`, en orden de documento. */
function filtrarAtomos(bloques: BloqueRico[], quitar: (a: Atomo) => boolean): void {
  for (const r of lineasEnOrden(bloques)) {
    for (let i = 0; i < r.length; i++) if (quitar(r[i])) { r.splice(i, 1); i--; }
  }
}

/** Los topes en su orden (§1.3, pasos 3 a 8). */
function aplicarTopes(entrada: BloqueRico[], descartes: Set<string>): BloqueRico[] {
  let bloques = entrada;
  // 3) un solo número
  let numeros = 0;
  filtrarAtomos(bloques, (a) => a.k === 'num' && ++numeros > 1);
  if (numeros > 1) descartes.add('numero-duplicado');
  // 4) imágenes por banda
  let imagenes = 0;
  filtrarAtomos(bloques, (a) => a.k === 'img' && ++imagenes > TOPES.imagenes);
  if (imagenes > TOPES.imagenes) descartes.add('imagenes-tope');
  // 5) alto apilado: fuera imágenes desde el final hasta que quepa
  while (altoApiladoMm(bloques) > TOPES.altoApiladoMm) {
    const lineas = lineasEnOrden(bloques);
    let quitada = false;
    for (let i = lineas.length - 1; i >= 0 && !quitada; i--) {
      const r = lineas[i];
      for (let j = r.length - 1; j >= 0; j--) if (r[j].k === 'img') { r.splice(j, 1); quitada = true; break; }
    }
    if (!quitada) break;
    descartes.add('imagenes-alto');
  }
  bloques = relimpiar(bloques);
  // 6) renglones visibles
  let presupuesto: number = TOPES.renglones;
  const conRenglones: BloqueRico[] = [];
  for (const b of bloques) {
    if (presupuesto <= 0) { descartes.add('renglones-tope'); continue; }
    if (b.tipo === 'p') { conRenglones.push(b); presupuesto -= 1; continue; }
    const n = Math.max(1, ...b.celdas.map((c) => c.lineas.length));
    if (n <= presupuesto) { conRenglones.push(b); presupuesto -= n; continue; }
    const queda = presupuesto;
    conRenglones.push({ ...b, celdas: b.celdas.map((c) => ({ ...c, lineas: c.lineas.slice(0, queda) })) });
    presupuesto = 0;
    descartes.add('renglones-tope');
  }
  bloques = conRenglones;
  // 7) texto visible: al llegar al tope se corta (sin partir un par sustituto) y lo demás se descarta
  let cuenta = 0;
  const conTexto: BloqueRico[] = [];
  const cortar = (r: Renglon): { r: Renglon; lleno: boolean } => {
    if (cuenta + r.length <= TOPES.texto) { cuenta += r.length; return { r, lleno: cuenta >= TOPES.texto }; }
    let k = TOPES.texto - cuenta;
    const ultimo = r[k - 1];
    if (k > 0 && ultimo?.k === 'txt' && /[\ud800-\udbff]/.test(ultimo.c)) k--;
    cuenta = TOPES.texto;
    descartes.add('texto-tope');
    return { r: r.slice(0, k), lleno: true };
  };
  let lleno = false;
  for (const b of bloques) {
    if (lleno) {
      if (lineasEnOrden([b]).some((r) => r.length)) descartes.add('texto-tope');
      continue;
    }
    if (b.tipo === 'p') {
      const c = cortar(b.linea);
      conTexto.push({ ...b, linea: c.r });
      lleno = c.lleno;
      continue;
    }
    const celdas: Celda[] = [];
    for (const cel of b.celdas) {
      if (lleno) {
        if (cel.lineas.some((r) => r.length)) descartes.add('texto-tope');
        celdas.push({ ...cel, lineas: [[]] });
        continue;
      }
      const lineas: Renglon[] = [];
      for (const r of cel.lineas) {
        if (lleno) { if (r.length) descartes.add('texto-tope'); continue; }
        const c = cortar(r);
        lineas.push(c.r);
        lleno = c.lleno;
      }
      celdas.push({ ...cel, lineas });
    }
    conTexto.push({ ...b, celdas });
  }
  // 8) otra vez los vacíos de los bordes
  return bordes(relimpiar(conTexto));
}

// ---------------------------------------------------------------------------
// Serialización
// ---------------------------------------------------------------------------

/** Escapado del texto del canónico: &, <, > y el NBSP; las comillas quedan tal cual. */
export const escaparTexto = (s: string): string =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\u00a0/g, '&nbsp;');
/** Al pintar también las comillas: el PDF y el Word buscan atributos con regex sobre el HTML entero (§3.1). */
const escaparPintado = (s: string): string => escaparTexto(s).replace(/"/g, '&quot;');

interface Modo {
  texto: (s: string) => string;
  imagen: (a: Extract<Atomo, { k: 'img' }>) => string;
  numero: () => string;
}

/** Las marcas siempre anidadas strong > em > u, abriendo y cerrando lo mínimo; el número nunca lleva marcas. */
function serializarLinea(r: Renglon, modo: Modo): string {
  let out = '';
  let tramo = '';
  const abiertas: (typeof MARCAS)[number][] = [];
  const soltar = () => { if (tramo) { out += modo.texto(tramo); tramo = ''; } };
  const ir = (m: number) => {
    const quiero = MARCAS.filter((k) => m & BIT_MARCA[k]);
    let comun = 0;
    while (comun < abiertas.length && comun < quiero.length && abiertas[comun] === quiero[comun]) comun++;
    if (comun === abiertas.length && comun === quiero.length) return;
    soltar();
    while (abiertas.length > comun) out += `</${abiertas.pop()}>`;
    for (const k of quiero.slice(comun)) { out += `<${k}>`; abiertas.push(k); }
  };
  for (const a of r) {
    if (a.k === 'txt') { ir(a.m); tramo += a.c; continue; }
    if (a.k === 'img') { ir(a.m); soltar(); out += modo.imagen(a); continue; }
    ir(0);
    soltar();
    out += modo.numero();
  }
  ir(0);
  soltar();
  return out;
}

const escaparAtributo = (s: string) => escaparPintado(s);

const MODO_CANONICO: Modo = {
  texto: escaparTexto,
  imagen: (a) => (a.marca === 'custom'
    ? `<img data-ez-img="custom" src="${escaparAtributo(a.src ?? '')}" style="height:${a.mm}mm" alt="">`
    : `<img data-ez-img="${a.marca}" style="height:${a.mm}mm" alt="">`),
  numero: () => TOKEN_NUMERO,
};

function serializarCanonico(bloques: BloqueRico[]): string {
  return bloques.map((b) => {
    if (b.tipo === 'p') return `<p style="text-align:${b.align}">${serializarLinea(b.linea, MODO_CANONICO)}</p>`;
    const celdas = b.celdas.map((c) => `<td style="text-align:${c.align}">${c.lineas.map((l) => `<p>${serializarLinea(l, MODO_CANONICO)}</p>`).join('')}</td>`);
    return `<table><tbody><tr>${celdas.join('')}</tr></tbody></table>`;
  }).join('');
}

// ---------------------------------------------------------------------------
// Texto de respaldo (§2.6)
// ---------------------------------------------------------------------------

// trim() también quita los NBSP de los bordes, como el back (es él quien guarda este texto).
const textoPlanoDe = (r: Renglon): string =>
  r.map((a) => (a.k === 'txt' ? a.c : '')).join('').replace(/ {2,}/g, ' ').trim();

function cortarSinPartir(s: string, n: number): string {
  if (s.length <= n) return s;
  const k = /[\ud800-\udbff]/.test(s[n - 1]) ? n - 1 : n;
  return s.slice(0, k);
}

function textoRespaldo(bloques: BloqueRico[]): string {
  const renglones = bloques.map((b) => (b.tipo === 'p'
    ? textoPlanoDe(b.linea)
    : b.celdas.map((c) => c.lineas.map(textoPlanoDe).filter(Boolean).join(' ')).filter(Boolean).join('   ')));
  return cortarSinPartir(renglones.filter(Boolean).slice(0, TOPES.respaldoRenglones).join('\n'), TOPES.respaldoTexto);
}

// ---------------------------------------------------------------------------
// normalizarBandaRica
// ---------------------------------------------------------------------------

const CACHE = new Map<string, BandaRica | null>();
const CACHE_MAX = 100;

function guardarEnCache(clave: string, r: BandaRica | null): BandaRica | null {
  // Un pegado enorme no se guarda: la caché es para lo que se repite en cada pintada.
  if (clave.length > 12000) return r;
  if (CACHE.size >= CACHE_MAX) CACHE.delete(CACHE.keys().next().value as string);
  CACHE.set(clave, r);
  return r;
}

function resultado(bloques: BloqueRico[], descartes: Set<string>): BandaRica {
  const todas = lineasEnOrden(bloques);
  const vacia = !todas.some((r) => r.length);
  const finales = vacia ? [] : bloques;
  const primero = finales[0];
  return {
    html: serializarCanonico(finales),
    texto: textoRespaldo(finales),
    alineacion: !primero ? 'left' : primero.tipo === 'p' ? primero.align : primero.celdas[0]?.align ?? 'left',
    tieneNumero: todas.some((r) => r.some((a) => a.k === 'num')),
    imagenes: todas.reduce((n, r) => n + r.filter((a) => a.k === 'img').length, 0),
    vacia,
    descartes: [...descartes],
    bloques: finales,
  };
}

/**
 * HTML cualquiera → banda canónica (§2.2). Null si la entrada no es texto,
 * pasa de 50.000 caracteres, el canónico pasa de 8.000 o no hay DOMPurify.
 * Con `topes: false` no aplica los pasos 3 a 7 (el editor lo usa para medir
 * antes de decidir si una imagen cabe). Los resultados se comparten (caché):
 * no se modifican.
 */
export function normalizarBandaRica(html: unknown, opciones: { topes?: boolean } = {}): BandaRica | null {
  if (typeof html !== 'string' || html.length > TOPES.entrada) return null;
  const conTopes = opciones.topes !== false;
  const clave = `${conTopes ? 1 : 0}|${origenExtra() ?? ''}|${html}`;
  if (CACHE.has(clave)) return CACHE.get(clave) ?? null;
  // Solo blancos ASCII: `trim()` también quitaría U+3000 o U+2003, que son texto (regla 7).
  if (!/[^\t\n\f\r ]/.test(html)) return guardarEnCache(clave, resultado([], new Set()));
  const frag = purificarAFragmento(html, CONFIG_PURIFICADOR);
  if (!frag) return null;
  try {
    const est: Estado = { descartes: new Set(), tablaVista: false, salida: [] };
    const rec = new Recolector(est.salida, false);
    recorrer(frag, { m: 0, prof: 0, enCelda: false }, rec, est);
    rec.fin();
    // 1) estructura
    let bloques: BloqueRico[] = [];
    for (const it of est.salida) {
      if (it.tipo === 'p') {
        for (const r of it.renglones) bloques.push({ tipo: 'p', align: it.align, linea: limpiar(r) });
        continue;
      }
      const [primera = [], ...resto] = it.filas;
      if (primera.length >= TOPES.celdasMin) {
        let celdas: Celda[] = primera;
        if (celdas.length > TOPES.celdasMax) {
          est.descartes.add('celdas');
          const ultima = { align: celdas[TOPES.celdasMax - 1].align, lineas: recortarCelda(celdas.slice(TOPES.celdasMax - 1).flatMap((c) => c.lineas)) };
          celdas = [...celdas.slice(0, TOPES.celdasMax - 1), ultima];
        }
        bloques.push({ tipo: 'tabla', celdas });
      } else {
        for (const c of primera) for (const l of c.lineas) bloques.push({ tipo: 'p', align: c.align, linea: l });
      }
      if (resto.length) est.descartes.add('filas');
      for (const fila of resto) for (const c of fila) for (const l of c.lineas) bloques.push({ tipo: 'p', align: c.align, linea: l });
    }
    // 2) vacíos de los bordes
    bloques = bordes(bloques);
    if (conTopes) bloques = aplicarTopes(bloques, est.descartes);
    const r = resultado(bloques, est.descartes);
    return guardarEnCache(clave, r.html.length > TOPES.html ? null : r);
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Pintar (§3.1): la misma cadena que el back
// ---------------------------------------------------------------------------

export interface ContextoPintado {
  /** pt, ya a medio punto (`medioPunto`). */
  tam: number;
  /** `familiaCss(fontFamily)`, sin comillas. */
  familia: string;
  numeroActivo: boolean;
  numero: () => string;
  variables: (s: string) => string;
  imagenPerfil: (marca: MarcaPerfil, mm: number) => string;
}

/**
 * HTML de la banda tal como lo pinta el aplicador: el mismo vocabulario de
 * siempre (p con estilo, tabla fija, img en mm, spans de numeración). Null
 * si el canónico no se puede sanear: quien llama pinta el `text`.
 */
export function pintarBandaRica(canonico: string, ctx: ContextoPintado): string | null {
  let r: BandaRica | null;
  try {
    r = normalizarBandaRica(canonico);
  } catch {
    r = null;
  }
  if (!r) return null;
  const modo: Modo = {
    texto: escaparPintado,
    imagen: (a) => {
      if (a.marca !== 'custom') return ctx.imagenPerfil(a.marca, a.mm);
      if (!esImagenPropia(a.src)) return '';
      return `<img src="${escaparAtributo(a.src ?? '')}" data-ez-img="custom" style="display:inline-block; height:${a.mm}mm; max-width:100%; object-fit:contain;" />`;
    },
    numero: () => (ctx.numeroActivo ? ctx.numero() : ''),
  };
  const letra = `font-size:${ctx.tam}pt; font-family:${ctx.familia};`;
  return r.bloques.map((b) => {
    if (b.tipo === 'p') {
      // Un párrafo con solo el número apagado no se pinta.
      if (!ctx.numeroActivo && b.linea.length === 1 && b.linea[0].k === 'num') return '';
      const interior = ctx.variables(serializarLinea(b.linea, modo));
      return `<p style="margin:0; text-align:${b.align}; ${letra}">${interior || '&#8203;'}</p>`;
    }
    const ancho = (100 / b.celdas.length).toFixed(4);
    const celdas = b.celdas.map((c) => {
      const interior = ctx.variables(c.lineas.map((l) => serializarLinea(l, modo)).join('<br />'));
      return `<td style="width:${ancho}%; padding:0; vertical-align:middle; text-align:${c.align}; ${letra}">${interior}</td>`;
    });
    return `<table style="width:100%; border-collapse:collapse; table-layout:fixed; border:none;"><tr>${celdas.join('')}</tr></table>`;
  }).join('');
}

/** `sanitizeBandaRicaSalida` con la lista blanca de imágenes de este front. */
export const salidaSegura = (html: string): string | null => sanitizeBandaRicaSalida(html, esImagenPropia, reUriPropia());

// ---------------------------------------------------------------------------
// Plantillas viejas (§6.2)
// ---------------------------------------------------------------------------

type ValoresBanda = Partial<Pick<FormatTemplateInput,
  'headerText' | 'headerAlign' | 'footerText' | 'headerColumns' | 'footerColumns' | 'headerEnabled' | 'footerEnabled' |
  'pageNumbering' | 'pageNumberPosition' | 'pageNumberFormat' | 'pageNumberStart' | 'mirrorsSource' | 'headerMode'>>;

const columnas = (v: ValoresBanda, banda: Banda): BandColumn[] => columnasDe(v as Parameters<typeof columnasDe>[0], banda);
const encendida = (v: ValoresBanda, banda: Banda) => !!(banda === 'header' ? v.headerEnabled : v.footerEnabled);
const textoPlano = (v: ValoresBanda, banda: Banda) => (banda === 'header' ? v.headerText : v.footerText) ?? '';

/** `escapar` del aplicador: no vuelve a escapar lo que ya parece una entidad. */
const escaparLegado = (s: string): string =>
  s.replace(/&(?!#?\w+;)/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** `escaparBanda` del aplicador: renglones recortados y, pasados cuatro, unidos al cuarto. */
function escaparBandaLegado(s: string, maxLineas = 4): string {
  const lineas = String(s ?? '').replace(/\r\n?/g, '\n').split('\n').map((l) => l.trim());
  const recortadas = lineas.length > maxLineas ? [...lineas.slice(0, maxLineas - 1), lineas.slice(maxLineas - 1).join(' ')] : lineas;
  return recortadas.map(escaparLegado).join('<br />');
}

/**
 * El interior que pinta hoy el aplicador para una banda sin `richHtml`, con
 * tres cambios: sin variables sustituidas, las imágenes del perfil sin URL y
 * el número de columna como token. El número automático no es contenido.
 */
export function htmlLegadoDeBanda(v: ValoresBanda, banda: Banda): string {
  const cols = columnas(v, banda);
  if (cols.length) {
    const celdas = cols.slice(0, 4).map((c) => {
      let interior = '';
      if (c.type === 'text') interior = htmlDeBloque(c) ?? escaparBandaLegado(c.text || '');
      else if (c.type === 'pagenum') interior = TOKEN_NUMERO;
      else if (c.type === 'image' && (c.image || c.imageUrl)) {
        const alto = altoImagenMm(c);
        if (c.imageUrl && esImagenPropia(c.imageUrl)) interior = `<img data-ez-img="custom" src="${escaparLegado(c.imageUrl)}" style="height:${alto}mm">`;
        else if (c.image) interior = `<img data-ez-img="${escaparLegado(c.image)}" style="height:${alto}mm">`;
      }
      return `<td style="text-align:${c.align || 'left'}">${interior}</td>`;
    });
    return `<table><tbody><tr>${celdas.join('')}</tr></tbody></table>`;
  }
  const t = textoPlano(v, banda);
  if (!t) return '';
  return banda === 'header'
    ? `<p style="text-align:${v.headerAlign ?? 'center'}; font-weight:bold">${escaparLegado(t)}</p>`
    : `<p style="text-align:center">${escaparLegado(t)}</p>`;
}

/** La banda vieja vista como canónico. Null si no se puede normalizar (sin DOMPurify). */
export function derivarBandaLegada(v: ValoresBanda, banda: Banda): string | null {
  const h = htmlLegadoDeBanda(v, banda);
  if (!h) return '';
  return normalizarBandaRica(h)?.html ?? null;
}

/** La columna rica de la banda (estructural): la primera, de texto y con `richHtml` string. */
export function columnaRica(v: ValoresBanda, banda: Banda): BandColumn | null {
  const c = columnas(v, banda)[0];
  return c && c.type === 'text' && typeof c.richHtml === 'string' ? c : null;
}

/** La banda rica con contenido (§3.2): la columna rica que sanea a algo. */
export function rica(v: ValoresBanda, banda: Banda): BandaRica | null {
  const c = columnaRica(v, banda);
  if (!c) return null;
  const r = normalizarBandaRica(c.richHtml);
  return r && !r.vacia ? r : null;
}

/**
 * Lo que el campo muestra y compara: el canónico de la banda rica o, si no
 * lo es (o su HTML no sirve), la vista derivada de la de siempre. Null solo
 * si no se puede normalizar (sin DOMPurify).
 */
export function htmlDeBanda(v: ValoresBanda, banda: Banda): string | null {
  const c = columnaRica(v, banda);
  if (c) {
    const r = normalizarBandaRica(c.richHtml);
    if (r) return r.html;
    // Un richHtml que no sanea: el back pinta su `text`, que es lo que se ve aquí.
    const { richHtml: _r, ...comoTexto } = c;
    return derivarBandaLegada({ ...v, [banda === 'header' ? 'headerColumns' : 'footerColumns']: [comoTexto] }, banda);
  }
  return derivarBandaLegada(v, banda);
}

/** Token de número en una banda rica encendida. */
export const tokenActivo = (v: ValoresBanda, banda: Banda): boolean =>
  encendida(v, banda) && !!rica(v, banda)?.tieneNumero;

/** Dónde va el número automático (§3.2). Sin bandas ricas es la condición de siempre. */
export function numeracionVaEn(v: ValoresBanda): Banda | null {
  if (!v.pageNumbering) return null;
  if (tokenActivo(v, 'header') || tokenActivo(v, 'footer')) return null;
  return v.pageNumberPosition === 'header-right' ? 'header' : 'footer';
}

/** ¿Sale la caja de la banda en la vista previa? Así siempre hay dónde hacer clic. Un espejo no tiene bandas. */
export const bandaVisible = (v: ValoresBanda, banda: Banda): boolean =>
  !v.mirrorsSource && (encendida(v, banda) || numeracionVaEn(v) === banda);

/**
 * ¿Se pinta el contenido de la banda (sin contar el número automático)? La
 * rica, solo encendida; la de siempre, como hoy: sus columnas aunque esté
 * apagada (si sale por la numeración) y el texto plano solo encendida.
 */
export function contenidoSale(v: ValoresBanda, banda: Banda): boolean {
  if (rica(v, banda)) return encendida(v, banda);
  if (columnas(v, banda).length) return true;
  return encendida(v, banda) && !!textoPlano(v, banda);
}

/** ¿Imprime esta banda un número de página propio (token o columna vieja)? */
export function numeroPropioEn(v: ValoresBanda, banda: Banda): boolean {
  if (rica(v, banda)) return tokenActivo(v, banda);
  const cols = columnas(v, banda);
  if (!cols.slice(0, 4).some((c) => c.type === 'pagenum')) return false;
  return (encendida(v, banda) && (!!textoPlano(v, banda) || cols.length > 0)) || numeracionVaEn(v) === banda;
}

/**
 * Cambio de la plantilla al editar la banda (§6.3). Vacía: columnas y texto
 * plano vacíos (con el texto plano lleno, el back volvería a pintarlo).
 * Con número y la numeración apagada, la enciende, salvo que la banda ya
 * tuviera su token (se apagó a propósito) o la norma la bloquee.
 */
export function cambioDeBandaRica(prev: ValoresBanda, banda: Banda, r: BandaRica, bloqueados: readonly string[] = []): Partial<FormatTemplateInput> {
  const cols = banda === 'header' ? 'headerColumns' : 'footerColumns';
  const plano = banda === 'header' ? 'headerText' : 'footerText';
  if (r.vacia) return { [cols]: [], [plano]: '' };
  const cambio: Partial<FormatTemplateInput> = { [cols]: [{ type: 'text', align: r.alineacion, text: r.texto, richHtml: r.html }] };
  const teniaToken = !!rica(prev, banda)?.tieneNumero;
  if (r.tieneNumero && !prev.pageNumbering && !teniaToken && !bloqueados.includes('pageNumbering')) cambio.pageNumbering = true;
  return cambio;
}

// ---------------------------------------------------------------------------
// Editor
// ---------------------------------------------------------------------------

export interface OpcionesEditor {
  /** Lo que se ve en el token (`muestraNumero`). */
  muestra: string;
  /** La numeración está apagada: el token sale atenuado. */
  apagado?: boolean;
  /** `src` del marcador de una imagen del perfil (PNG generado en el navegador). */
  marcador?: (marca: MarcaPerfil) => string | null;
  /** Párrafos de relleno antes y después de una tabla en los bordes, para poder salir de ella. */
  relleno?: boolean;
}

/**
 * El canónico tal como se abre en el editor: el token con su muestra y no
 * editable, las imágenes del perfil con su marcador, y un párrafo vacío a
 * cada lado de una tabla que toca un borde. `normalizar(aEditor(x)) === x`.
 */
function modoEditor(o: OpcionesEditor): Modo {
  return {
    texto: escaparTexto,
    imagen: (a) => {
      if (a.marca === 'custom') return `<img data-ez-img="custom" src="${escaparAtributo(a.src ?? '')}" style="height:${a.mm}mm" alt="">`;
      const src = o.marcador?.(a.marca);
      return `<img data-ez-img="${a.marca}"${src ? ` src="${escaparAtributo(src)}"` : ''} style="height:${a.mm}mm" alt="">`;
    },
    numero: () => `<span data-ez-campo="numero" class="mceNonEditable${o.apagado ? ' ez-num-apagado' : ''}" contenteditable="false">${escaparTexto(o.muestra)}</span>`,
  };
}

function bloqueEditor(b: BloqueRico, modo: Modo, conAlineacion = true): string {
  if (b.tipo === 'p') return `<p${conAlineacion ? ` style="text-align:${b.align}"` : ''}>${serializarLinea(b.linea, modo)}</p>`;
  const celdas = b.celdas.map((c) => `<td style="text-align:${c.align}">${c.lineas.map((l) => `<p>${serializarLinea(l, modo)}</p>`).join('')}</td>`);
  return `<table><tbody><tr>${celdas.join('')}</tr></tbody></table>`;
}

export function aEditor(html: string, o: OpcionesEditor): string {
  const r = normalizarBandaRica(html);
  if (!r) return '';
  const modo = modoEditor(o);
  const relleno = '<p style="text-align:left"></p>';
  const partes = r.bloques.map((b) => bloqueEditor(b, modo));
  if (o.relleno !== false && r.bloques.length) {
    if (r.bloques[0].tipo === 'tabla') partes.unshift(relleno);
    if (r.bloques[r.bloques.length - 1].tipo === 'tabla') partes.push(relleno);
  }
  return partes.join('');
}

/**
 * Lo pegado, ya canónico y en la forma del editor: sin relleno, un solo
 * párrafo como contenido en línea (se une al renglón del cursor) y, dentro
 * de una celda, las tablas aplanadas a renglones. Null si no cabe en el tope.
 */
export function pegadoParaEditor(html: string, o: OpcionesEditor & { enCelda?: boolean }): string | null {
  const r = normalizarBandaRica(html);
  if (!r) return null;
  const bloques: BloqueRico[] = o.enCelda
    ? r.bloques.flatMap((b): BloqueRico[] => (b.tipo === 'p' ? [b] : b.celdas.flatMap((c) => c.lineas.map((linea): Parrafo => ({ tipo: 'p', align: c.align, linea })))))
    : r.bloques;
  const modo = modoEditor(o);
  if (bloques.length === 1 && bloques[0].tipo === 'p') return serializarLinea(bloques[0].linea, modo);
  return bloques.map((b) => bloqueEditor(b, modo, !o.enCelda)).join('');
}

/**
 * ¿La banda es texto llano? Solo párrafos (sin tabla, imágenes ni número),
 * todos con la misma alineación y todo el texto con la misma máscara. Es lo
 * único que el campo simple de respaldo sabe editar sin perder formato.
 */
export function esBandaLlana(r: BandaRica): boolean {
  if (r.bloques.some((b) => b.tipo !== 'p')) return false;
  const parrafos = r.bloques as Parrafo[];
  if (new Set(parrafos.map((p) => p.align)).size > 1) return false;
  const mascaras = new Set<number>();
  for (const p of parrafos) {
    for (const a of p.linea) {
      if (a.k !== 'txt') return false;
      if (a.c !== ' ' && a.c !== '\u00a0') mascaras.add(a.m);
    }
  }
  return mascaras.size <= 1;
}

/** Máscara y alineación de una banda llana, para reconstruirla desde el campo simple. */
export function formaLlana(r: BandaRica): { align: Alineacion; m: number } {
  const p = r.bloques.find((x): x is Parrafo => x.tipo === 'p');
  const a = p?.linea.find((x) => x.k === 'txt' && x.c !== ' ' && x.c !== '\u00a0');
  return { align: p?.align ?? 'left', m: a && a.k === 'txt' ? a.m : 0 };
}

/** El canónico de una banda llana escrita en el campo simple: un párrafo por renglón. */
export function htmlBandaLlana(texto: string, align: Alineacion, m: number): string {
  const abre = MARCAS.filter((k) => m & BIT_MARCA[k]);
  return String(texto ?? '').replace(/\r\n?/g, '\n').split('\n').map((l) => {
    const interior = l ? `${abre.map((k) => `<${k}>`).join('')}${escaparTexto(l)}${[...abre].reverse().map((k) => `</${k}>`).join('')}` : '';
    return `<p style="text-align:${align}">${interior}</p>`;
  }).join('');
}

/** El texto de una banda llana para el campo simple: un renglón por párrafo. */
export const textoDeBandaLlana = (r: BandaRica): string =>
  (r.bloques as Parrafo[]).map((p) => p.linea.map((a) => (a.k === 'txt' ? a.c : '')).join('')).join('\n');

// ---------------------------------------------------------------------------
// Medidas, letra y número de muestra
// ---------------------------------------------------------------------------

/** Ancho de la banda en mm: el de la hoja menos los márgenes laterales y la encuadernación, como el back. */
export function anchoBandaMm(v: Pick<FormatTemplateInput, 'pageFormat' | 'pageOrientation' | 'marginLeftMm' | 'marginRightMm' | 'bindingMarginMm'>): number {
  const h = hojaMm(v.pageFormat, v.pageOrientation);
  return h.ancho - ((Number(v.marginLeftMm) || 0) + (Number(v.bindingMarginMm) || 0)) - (Number(v.marginRightMm) || 0);
}

export const medioPunto = (pt: number): number => Math.round(pt * 2) / 2;

/** Letra de la banda en pt: el encabezado, la suya o la del cuerpo; el pie, la del cuerpo con tope de 10. */
export function tamBandaPt(v: Pick<FormatTemplateInput, 'fontSizePt' | 'headerFontSizePt'>, banda: Banda): number {
  return banda === 'header' ? medioPunto(v.headerFontSizePt ?? v.fontSizePt) : medioPunto(Math.min(v.fontSizePt, 10));
}

const GENERICA_LIBRE = { serifa: 'serif', sans: 'sans-serif', mono: 'monospace', manuscrita: 'cursive' } as const;

/**
 * `familiaCss` del aplicador: sin comillas (el Word cortaba la letra en
 * `&#39;`), salvo cuando el nombre no es CSS válido sin ellas ('Source Serif 4',
 * 'Source Sans 3'): sin comillas la declaración entera se descarta. Las libres
 * del selector llevan su genérica.
 */
export function familiaCss(nombre: string): string {
  const limpio = String(nombre ?? '').replace(/["']/g, '').trim();
  const libre = FUENTES_LIBRES.find((f) => f.titulo.toLowerCase() === limpio.toLowerCase());
  const serif = /times|georgia|garamond|book|cambria|palatino|serif/i.test(limpio) && !/sans/i.test(limpio);
  const generica = libre && libre.grupo !== 'office' ? GENERICA_LIBRE[libre.grupo] : serif ? 'serif' : 'sans-serif';
  return `${nombreCss(limpio)}, ${generica}`;
}

/**
 * `familiaCss` para escribirla dentro de un `style` o de una hoja de estilo
 * del navegador: la letra es texto libre de la plantilla, y un `;` o una
 * llave añadirían declaraciones (por ejemplo, una capa fija sobre la página).
 */
export const familiaEnEstilo = (nombre: string): string => familiaCss(nombre).replace(/[{}<>;\\]/g, '');

type Numeracion = Partial<Pick<FormatTemplateInput, 'pageNumberFormat' | 'pageNumberStart'>>;

/** El número tal como lo imprime el back en la primera hoja (con dos hojas de muestra). */
export function muestraNumero(v: Numeracion): string {
  const i = v.pageNumberStart ?? 1;
  if (v.pageNumberFormat === '00n') return String(i).padStart(3, '0');
  if (v.pageNumberFormat === 'n') return String(i);
  return `Página ${i} de ${i + 1}`;
}

/** Port literal de `numeracion()` del aplicador, con dos hojas de muestra (`total = 2`). */
export function numeracionMuestra(v: Numeracion, total = 2): string {
  const inicio = v.pageNumberStart ?? 1;
  const fin = inicio + Math.max(0, total - 1);
  const cifras = String(fin).length;
  const reserva = `style="display:inline-block; min-width:${cifras}ch; text-align:center;"`;
  switch (v.pageNumberFormat) {
    case '00n':
      return `<span data-ez-pagenum="${inicio}" data-ez-ancho="3" ${reserva}>${String(inicio).padStart(3, '0')}</span>`;
    case 'n':
      return `<span data-ez-pagenum="${inicio}" data-ez-ancho="1" ${reserva}>${inicio}</span>`;
    case 'n_de_m':
    default: {
      const totalHtml = inicio === 1 ? `<span data-ez-numpages="${fin}" ${reserva}>${fin}</span>` : `${fin}`;
      return `Página <span data-ez-pagenum="${inicio}" data-ez-ancho="1" ${reserva}>${inicio}</span> de ${totalHtml}`;
    }
  }
}

/** Resumen de la tarjeta (F9): la primera línea de la banda. */
export function resumenBanda(tpl: ValoresBanda, banda: Banda): string {
  const primera = (s: string | null | undefined) => String(s ?? '').split('\n')[0].trim();
  const c = columnaRica(tpl, banda);
  if (c && primera(c.text)) return primera(c.text);
  const plano = primera(textoPlano(tpl, banda));
  if (plano) return plano;
  const t = columnas(tpl, banda).find((x) => x.type === 'text' && primera(x.text));
  return t ? primera(t.text) : '—';
}

const richDe = (cols: BandColumn[] | null | undefined): string | null => {
  const c = cols?.[0];
  return c && c.type === 'text' && typeof c.richHtml === 'string' ? c.richHtml : null;
};
const contarImagenes = (html: string | null) => (html?.match(/<img\b/gi) ?? []).length;

/**
 * Guarda ante un processing-ms viejo (§10.5): si se mandó una banda rica y
 * la plantilla guardada vuelve sin ella, el back solo guardó su texto.
 * 'perdida' = el formulario no se da por guardado; 'imagenes' = el back
 * descartó alguna imagen (manda su HTML); null = todo bien.
 */
export function comprobarBandasGuardadas(
  enviado: Pick<FormatTemplateInput, 'headerColumns' | 'footerColumns'>,
  guardada: Pick<FormatTemplateInput, 'headerColumns' | 'footerColumns'> | null,
): 'perdida' | 'imagenes' | null {
  if (!guardada) return null;
  let aviso: 'imagenes' | null = null;
  const pares = [[enviado.headerColumns, guardada.headerColumns], [enviado.footerColumns, guardada.footerColumns]] as const;
  for (const [mandadas, vueltas] of pares) {
    const ida = richDe(mandadas);
    if (!ida) continue;
    const vuelta = richDe(vueltas);
    if (vuelta == null) return 'perdida';
    if (vuelta !== ida && contarImagenes(vuelta) < contarImagenes(ida)) aviso = 'imagenes';
  }
  return aviso;
}
